/**
 * Shared logic for the small internal registries under Settings — Agents and
 * Banks (SOUND-672, backend SOUND-671).
 *
 * Both collections carry the SAME minimal shape — `{ name, status, notes,
 * tags[] }` — deliberately, so this file is the one place the parsing and
 * deriving lives. That matters more than usual here: this repo has no
 * component-test harness, so anything left inline in a page body is
 * unverifiable except by a human clicking it.
 *
 * The registries exist because the seed only covers what a Notion export
 * happened to contain; the real lists are longer and the CPO maintains them
 * directly. So the surface has to tolerate rows the seed never anticipated.
 */

/** The only two statuses the API accepts. `inactive` is what DELETE sets. */
export const REGISTRY_STATUSES = ["active", "inactive"];

/**
 * A row is active UNLESS it explicitly says otherwise.
 *
 * Deliberately not `status === "active"`: deactivation is the only delete on
 * these collections, so a row that fails this check disappears from the default
 * view. A missing status, or some future third value, must not be enough to
 * make a record vanish — the failure mode should be "shows up unexpectedly",
 * never "silently gone".
 */
export function isActiveRow(row) {
  return (row?.status ?? "active") !== "inactive";
}

/**
 * Free-text tag entry → a clean array.
 *
 * Accepts either the comma-separated string the input holds or an array (a
 * record loaded from the API), so callers never have to know which they have.
 * Dedupes case-INSENSITIVELY but keeps the first spelling: "Evolve, evolve" is
 * one tag, and the one the user typed first is the one that survives.
 */
export function parseTags(input) {
  const raw = Array.isArray(input) ? input : String(input ?? "").split(",");
  const seen = new Set();
  const out = [];
  raw.forEach((t) => {
    const tag = String(t ?? "").trim();
    if (!tag) return;
    const key = tag.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(tag);
  });
  return out;
}

/** Array → the comma-separated string the tag input renders. */
export function formatTags(tags) {
  return parseTags(tags).join(", ");
}

/**
 * Form state → the request body.
 *
 * `notes` and `tags` are always present, never omitted-when-empty: an empty
 * string and an empty array are how the user CLEARS them. Dropping the key
 * instead would make a note impossible to delete once written.
 */
export function buildRegistryPayload({ name, status, notes, tags } = {}) {
  const payload = {
    name: String(name ?? "").trim(),
    notes: String(notes ?? "").trim(),
    tags: parseTags(tags),
  };
  if (status !== undefined) {
    payload.status = REGISTRY_STATUSES.includes(status) ? status : "active";
  }
  return payload;
}

/**
 * Client-side duplicate guard. The server stays authoritative; this only stops
 * the obvious "GOAT" / "goat" pair from being created by accident, which on a
 * registry that MID applications will point at is a real data problem rather
 * than a cosmetic one.
 *
 * `selfId` excludes the record being edited so renaming a row to its own name
 * is not a conflict.
 */
export function findDuplicateName(rows, name, idKey, selfId) {
  const needle = String(name ?? "").trim().toLowerCase();
  if (!needle) return null;
  return (
    (rows || []).find(
      (r) =>
        String(r?.name ?? "").trim().toLowerCase() === needle &&
        (!selfId || r?.[idKey] !== selfId)
    ) || null
  );
}

/**
 * The rendered row set: status filter, then search, then order.
 *
 * Search spans name, notes AND tags — tags are the only structure these records
 * have, so a registry you cannot search by tag is a flat list.
 *
 * Order is active-first, then name. Inactive rows sink rather than hide: the
 * ticket is explicit that deactivated records stay visible and filterable.
 */
export function filterRegistryRows(rows, { search = "", showInactive = false } = {}) {
  const q = String(search ?? "").trim().toLowerCase();
  return (rows || [])
    .filter((r) => showInactive || isActiveRow(r))
    .filter((r) => {
      if (!q) return true;
      const haystack = [r?.name, r?.notes, ...(parseTags(r?.tags))]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    })
    .sort((a, b) => {
      const aActive = isActiveRow(a) ? 0 : 1;
      const bActive = isActiveRow(b) ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      return String(a?.name ?? "").localeCompare(String(b?.name ?? ""));
    });
}
